import { EllipsisOutlined } from '@ant-design/icons';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import { MainText } from '@/components/Typography/NamedTypo';
import DashboardCardStyles from '@/features/Dashboard/components/styles/DashboardCardStyles';
import { Dashboard2ColumnType } from '@/features/Dashboard/types';

import useReportStore from '../../store/ReportStore';
import type { MappedReportT, ReportMappedColumnT } from '../../types';
import ReportAnalysisItem from '../ReportItem/ReportAnalysisItem';
import ReportChartItem from '../ReportItem/ReportChartItem';
import ReportFeedbackGalleryItem from '../ReportItem/ReportFeedbackGalleryItem';
import ReportTextItem from '../ReportItem/ReportTextItem';

const { DashboardCardHeader, StyledDashboardCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

export type ReportColumnProps = {
  report: MappedReportT;
  column: ReportMappedColumnT;
  width: string;
  index: number;
};

const Fallback = (props: ReportColumnProps) => {
  const removeColumn = useReportStore((state) => state.removeColumn);
  return (
    <StyledDashboardCardWrapper isDragging={false} width={props.width}>
      <StyledDashboardCard>
        <DashboardCardHeader j="flex-end" a="center">
          <Menu
            triggerNode={<IconButton icon={<EllipsisOutlined />} />}
            options={[
              {
                label: 'Delete',
                onClick: () => {
                  removeColumn(props.column.row_alias, props.column);
                },
              },
            ]}
          />
        </DashboardCardHeader>
        <CenterFlex f={1}>
          <MainText dim>Error occurred</MainText>
        </CenterFlex>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const ReportColumn = (props: ReportColumnProps) => {
  const { column } = props;

  switch (column.item.type) {
    case Dashboard2ColumnType.DASHBOARD_COLUMN_CHART:
      return (
        <ErrorBoundary fallback={<Fallback {...props} />}>
          <ReportChartItem {...props} />
        </ErrorBoundary>
      );
    case Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS:
      return (
        <ErrorBoundary fallback={<Fallback {...props} />}>
          <ReportAnalysisItem {...props} />
        </ErrorBoundary>
      );
    case Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX:
      return <ReportTextItem {...props} />;
    case Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY:
      return (
        <ErrorBoundary fallback={<Fallback {...props} />}>
          <ReportFeedbackGalleryItem {...props} />
        </ErrorBoundary>
      );
    default:
      return null;
  }
};

export default ReportColumn;
